import {
  type Hex,
  type HexLayout,
  type PixelPoint,
  axialToPixel,
  pixelToAxial,
} from '../../core/hex';

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export interface CameraTransform {
  readonly x: number;
  readonly y: number;
}

/**
 * World-container offset that centres `target` in the viewport.
 *
 * The world container holds hex graphics positioned at their natural pixel
 * coordinates (per `axialToPixel`). Setting `worldContainer.position` to this
 * value puts the camera target at the viewport centre.
 */
export function cameraTransform(
  target: Hex,
  viewport: Viewport,
  layout: HexLayout,
): CameraTransform {
  const centre = axialToPixel(target, layout);
  return {
    x: viewport.width / 2 - centre.x,
    y: viewport.height / 2 - centre.y,
  };
}

/** Convert a viewport-relative pointer position into world-space pixel coordinates. */
export function screenToWorld(
  screen: PixelPoint,
  target: Hex,
  viewport: Viewport,
  layout: HexLayout,
): PixelPoint {
  const t = cameraTransform(target, viewport, layout);
  return { x: screen.x - t.x, y: screen.y - t.y };
}

/** Convert a viewport-relative pointer position straight to axial hex coordinates. */
export function screenToAxial(
  screen: PixelPoint,
  target: Hex,
  viewport: Viewport,
  layout: HexLayout,
): Hex {
  return pixelToAxial(screenToWorld(screen, target, viewport, layout), layout);
}

/**
 * Reverse the world-container transform directly from its current pixel offset
 * — used while the camera is mid-tween, when the logical `cameraTarget` hex no
 * longer matches the visual offset. Equivalent to `screenToAxial` once the
 * tween has settled.
 */
export function screenToAxialAt(
  screen: PixelPoint,
  transform: CameraTransform,
  layout: HexLayout,
): Hex {
  return pixelToAxial({ x: screen.x - transform.x, y: screen.y - transform.y }, layout);
}
